"use client";

import { createElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  FormProvider,
  useForm,
  type Control,
  type FieldErrors,
  type FieldValues,
} from 'react-hook-form';
import { useAppRouter as useRouter } from '@/lib/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { TaskHeader } from '@/components/task/task-header';
import { TaskFooter } from '@/components/task/task-footer';
import { LoadingStates } from '@/components/loading-states';
import { TaskPageSkeleton } from '@/components/skeletons/task-page-skeleton';
import DisabledFormWrapper from '@/components/task/disabled-form-wrapper';
import { InheritedFormModeProvider } from '@/forms/shared/components';
import { cn } from '@/lib/utils';
import { taskToast } from '@/lib/task-toast';
import { getDefaultValuesFromSchema } from '@/lib/form-utilities';
import {
  useFormLoading,
  useFormSaving,
  useFormSubmitting,
  useFormError,
  useFormComponent,
  useSuccessComponent,
  useFormInfo,
  useFormSchema,
  useSubmissionState,
  useSubmittedData,
  useFormVariables,
  useFormPreloadedData,
  useLoadForm,
  useInspectRemoteFormChange,
  useSaveForm,
  useSubmitForm,
  usePersistTaskFormDraft,
  useClearForm,
  useClearFormActionError,
  useFormStore,
} from '@/stores/form-store';
import {
  consumePendingLocalTaskAction,
  hasPendingLocalTaskAction,
  markPendingLocalTaskAction,
  useSelectTask,
  useLoadTasks,
  useClearCurrentTask,
  useTaskStoreOptimized,
} from '@/stores/task-store';
import { CamundaUserService } from '@/lib/camunda';
import { trackValidationFailed } from '@/lib/telemetry/bpm-telemetry';
import { subscribeTaskRealtimeEvents } from '@/lib/task-realtime/client';
import { TaskReconciliationScheduler } from '@/lib/task-realtime/reconciliation-scheduler';
import { decideRemoteFormRefresh } from '@/lib/task-realtime/form-refresh-policy';
import {
  handleTerminalTaskSubmitResult,
  reconcileRealtimeFormReload,
} from '@/lib/task-realtime/task-page-orchestration';
import type { TaskInactiveNotice } from '@/stores/task-store.types';

interface TaskPageProps {
  params: {
    taskId: string;
  };
}

function shouldReloadFormAfterRealtimeRefresh(formKey: string | null): boolean {
  return formKey === 'carta' || formKey === 'envio-buromail';
}

function getTaskAssigneeLabel(task: { assignee?: string | null; assignee_fullname?: string | null }): string {
  return task.assignee_fullname || task.assignee || 'otra persona';
}

const genericInactiveTaskNotice: TaskInactiveNotice = {
  reason: 'not_found',
  title: 'Esta tarea ya no esta disponible',
  description: 'Camunda no la encontro como tarea activa. Puede tratarse de un enlace antiguo o de un cambio de permisos.',
};

function appendErrorCode(description: string, eventId?: string): string {
  return eventId ? `${description}\nCódigo de error: ${eventId}` : description;
}

function collectValidationErrorFields(errors: FieldErrors<FieldValues>, prefix = ''): string[] {
  return Object.entries(errors).flatMap(([field, value]) => {
    const path = prefix ? `${prefix}.${field}` : field;
    if (!value || Object.prototype.toString.call(value) !== '[object Object]') {
      return [path];
    }
    if (
      Object.prototype.hasOwnProperty.call(value, 'message')
      || Object.prototype.hasOwnProperty.call(value, 'type')
    ) {
      return [path];
    }
    return collectValidationErrorFields(Object.fromEntries(Object.entries(value)), path);
  });
}

function getRegisteredFormFields(control: Control<FieldValues>): string[] {
  return Array.from(control._names.mount).sort();
}

function getDraftPersistenceDescription(saved: boolean): string {
  return saved
    ? 'Hemos guardado los datos actuales en Camunda para que puedas recuperarlos al volver a abrir la tarea.'
    : 'No hemos podido guardar el borrador en Camunda. No cierres ni recargues esta pagina si necesitas conservar los cambios.';
}

export default function TaskPage({ params }: TaskPageProps) {
  const { taskId } = params;
  const { replace } = useRouter();
  const [inactiveTaskNotice, setInactiveTaskNotice] = useState<TaskInactiveNotice | null>(null);

  const isFormLoading = useFormLoading();
  const isSaving = useFormSaving();
  const isSubmitting = useFormSubmitting();
  const formError = useFormError();
  const FormComponent = useFormComponent();
  const SuccessComponent = useSuccessComponent();
  const formInfo = useFormInfo();
  const schema = useFormSchema();
  const submissionState = useSubmissionState();
  const submittedData = useSubmittedData();
  const variables = useFormVariables();
  const preloadedData = useFormPreloadedData();
  const formKey = formInfo?.key ?? null;

  const { currentTask, isTaskLoading, taskError } = useTaskStoreOptimized();
  const selectTask = useSelectTask();
  const loadTasks = useLoadTasks();
  const clearCurrentTask = useClearCurrentTask();

  const loadForm = useLoadForm();
  const inspectRemoteFormChange = useInspectRemoteFormChange();
  const currentTaskRef = useRef(currentTask);
  const isFormDirtyRef = useRef(false);
  const remoteInspectionFailedRef = useRef(false);
  const saveForm = useSaveForm();
  const submitForm = useSubmitForm();
  const persistTaskFormDraft = usePersistTaskFormDraft();
  const clearForm = useClearForm();
  const clearActionError = useClearFormActionError();

  const canInteractWithTask = (() => {
    if (!currentTask || currentTask.id !== taskId) return false;

    const currentUserId = CamundaUserService.getCurrentUserId();
    if (!currentUserId) return false;

    return currentTask.assignee === currentUserId;
  })();

  const isLoading = isTaskLoading || isFormLoading;
  const error = taskError || formError;
  const isDisabled = !canInteractWithTask;

  const defaultValues = (() => {
    if (variables) {
      return variables;
    }
    if (schema && schema instanceof z.ZodObject) {
      return getDefaultValuesFromSchema(schema);
    }
    return {};
  })();

  const form = useForm({
    mode: 'onChange',
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  });
  const { dirtyFields, touchedFields, isDirty } = form.formState;

  useEffect(() => {
    currentTaskRef.current = currentTask;
  }, [currentTask]);

  useLayoutEffect(() => {
    isFormDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!taskId) return;

    let cancelled = false;

    selectTask(taskId)
      .then(async (result) => {
        if (cancelled) {
          return;
        }
        if (result.status !== 'ready') {
          clearForm();
          if (result.status === 'not_active') {
            setInactiveTaskNotice(result.notice);
            taskToast.info(result.notice.title, {
              description: result.notice.description,
            });
          }
          return;
        }
        setInactiveTaskNotice(null);
        await loadForm(taskId);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Error preparing task form:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [taskId, selectTask, loadForm, clearForm]);

  useEffect(() => {
    if (!taskId) return;

    const scheduler = new TaskReconciliationScheduler(
      async (reasons) => {
        const shouldInspectForm = reasons.some((reason) => (
          reason === 'invalidation' || reason.startsWith('upsert:')
        ));
        const previousTask = currentTaskRef.current;
        const result = await selectTask(taskId);

        if (result.status === 'not_active') {
          if (hasPendingLocalTaskAction(taskId, ['complete'])) {
            return;
          }
          clearCurrentTask();
          taskToast.info(result.notice.title, {
            description: result.notice.description,
          });
          replace('/task-list/tasks');
          return;
        }

        if (result.status !== 'ready') {
          return;
        }

        const currentUserId = CamundaUserService.getCurrentUserId();
        const localAction = consumePendingLocalTaskAction(
          taskId,
          ['claim', 'unclaim', 'reassign'],
          result.task.assignee,
        );

        if (result.task.assignee && result.task.assignee !== currentUserId) {
          const assigneeChanged = previousTask?.assignee !== result.task.assignee;
          if (assigneeChanged && !localAction) {
            taskToast.info(`La tarea ahora es de ${getTaskAssigneeLabel(result.task)}`, {
              description: 'Volvemos a tu lista con el estado actualizado desde Camunda.',
            });
          }
          if (assigneeChanged) {
            replace('/task-list/tasks');
          }
          return;
        }

        if (result.task.assignee === currentUserId && previousTask?.assignee !== currentUserId && !localAction) {
          taskToast.info('La tarea ahora es tuya', {
            description: 'Ya puedes editarla.',
          });
        }

        if (!result.task.assignee && previousTask?.assignee === currentUserId && !localAction) {
          taskToast.info('La tarea quedo libre', {
            description: 'Ya no esta asignada a ti. Obtenla de nuevo si necesitas editarla.',
          });
        }

        if (shouldInspectForm && shouldReloadFormAfterRealtimeRefresh(formKey)) {
          try {
            const remoteChange = await inspectRemoteFormChange(taskId);
            remoteInspectionFailedRef.current = false;
            const refreshDecision = decideRemoteFormRefresh(
              remoteChange,
              isFormDirtyRef.current,
            );
            if (refreshDecision === 'unchanged') {
              return;
            }
            if (refreshDecision !== 'reload') {
              if (refreshDecision === 'preserve-and-notify') {
                taskToast.warning('La tarea cambio en Camunda', {
                  description: 'Conservamos tus cambios locales. Guarda lo que necesites y recarga la tarea para revisar los datos nuevos.',
                  duration: 10000,
                });
              }
              return;
            }

            await reconcileRealtimeFormReload({
              taskId,
              loadForm,
              getFormState: useFormStore.getState,
              onApplied: () => {
                if (!localAction) {
                  taskToast.info('Datos de la tarea actualizados', {
                    description: 'Hemos recargado el formulario con los cambios de Camunda.',
                  });
                }
              },
              onFailed: () => {
                taskToast.error('No se pudo recargar el formulario', {
                  description: 'La tarea cambio en Camunda, pero no pudimos traer los datos nuevos.',
                });
              },
            });
          } catch (error) {
            console.error('Error checking current task form variables:', error);
            if (!remoteInspectionFailedRef.current) {
              remoteInspectionFailedRef.current = true;
              taskToast.error('No se pudo comprobar el formulario', {
                description: 'No pudimos verificar si la tarea abierta cambio en Camunda.',
              });
            }
          }
        }
      },
      {
        delayMs: 250,
        jitterMs: 750,
        minIntervalMs: 2_000,
      },
    );
    const unsubscribe = subscribeTaskRealtimeEvents((event) => {
      if (event.type === 'TASKS_INVALIDATED') {
        scheduler.schedule('invalidation');
      } else if (event.taskId === taskId) {
        scheduler.schedule(`${event.type === 'TASK_UPSERT' ? 'upsert' : 'remove'}:${event.eventType}`);
      }
    });

    return () => {
      unsubscribe();
      scheduler.dispose();
    };
  }, [
    taskId,
    formKey,
    replace,
    clearCurrentTask,
    loadForm,
    inspectRemoteFormChange,
    selectTask,
  ]);

  useLayoutEffect(() => {
    if (schema && variables) form.reset(variables);
  }, [schema, variables, form]);

  const handleTaskConflict = async (action: 'guardar' | 'enviar', eventId?: string) => {
    void loadTasks({ silent: true });
    const refreshed = await selectTask(taskId);

    if (refreshed.status === 'ready') {
      const currentUserId = CamundaUserService.getCurrentUserId();
      if (refreshed.task.assignee === currentUserId) {
        await loadForm(taskId);
        taskToast.warning('La tarea cambio en Camunda', {
          description: appendErrorCode(
            `No se pudo ${action} porque detectamos otro cambio concurrente. Revisa los datos actualizados antes de continuar.`,
            eventId,
          ),
        });
        return;
      }

      clearCurrentTask();
      if (!refreshed.task.assignee) {
        taskToast.info('La tarea quedo libre', {
          description: 'Volvemos a tu lista con el estado actualizado desde Camunda.',
        });
        replace('/task-list/tasks');
        return;
      }

      taskToast.info(`La tarea ahora es de ${getTaskAssigneeLabel(refreshed.task)}`, {
        description: 'Volvemos a tu lista con el estado actualizado desde Camunda.',
      });
      replace('/task-list/tasks');
      return;
    }

    clearCurrentTask();
    const notice = refreshed.status === 'not_active' ? refreshed.notice : genericInactiveTaskNotice;
    taskToast.info(notice.title, { description: notice.description });
    replace('/task-list/tasks');
  };

  const showInactiveTaskAndLeave = async () => {
    const result = await selectTask(taskId);
    const notice = result.status === 'not_active' ? result.notice : genericInactiveTaskNotice;
    clearCurrentTask();
    void loadTasks({ silent: true });
    taskToast.info(notice.title, { description: notice.description });
    replace('/task-list/tasks');
  };

  const handleSave = async () => {
    clearActionError();
    const formData = form.getValues();
    const result = await saveForm(taskId, formData);

    if (result.ok) {
      taskToast.success('Cambios guardados', {
        description: 'La tarea sigue abierta para continuar trabajando.',
      });
      return;
    }

    if (result.reason === 'task_not_active') {
      await showInactiveTaskAndLeave();
      return;
    }

    if (result.reason === 'conflict') {
      await handleTaskConflict('guardar', result.eventId);
      return;
    }

    if (result.reason === 'auth_error') {
      taskToast.warning('Sesion caducada', {
        description: appendErrorCode('Vuelve a iniciar sesion antes de guardar.', result.eventId),
      });
      return;
    }

    if (result.reason === 'process_error') {
      taskToast.error('Camunda no pudo guardar', {
        description: appendErrorCode(result.message, result.eventId),
        duration: 10000,
      });
      return;
    }

    taskToast.error('No se pudo guardar', {
      description: appendErrorCode('La tarea sigue abierta. Revisa el aviso del formulario e intentalo de nuevo.', result.eventId),
    });
  };

  const onSubmit = async (data: FieldValues) => {
    clearActionError();
    markPendingLocalTaskAction(taskId, 'complete');
    const result = await submitForm(taskId, data);

    if (handleTerminalTaskSubmitResult(result, {
      consumePending: () => consumePendingLocalTaskAction(taskId, ['complete']),
      refreshTasks: () => { void loadTasks({ silent: true }); },
      hasSuccessComponent: Boolean(SuccessComponent),
      clearCurrentTask,
      notifySuccess: () => {
        taskToast.success('Tarea enviada', {
          description: 'Camunda acepto el envio correctamente.',
        });
      },
      leaveTask: () => replace('/task-list/tasks'),
    })) {
      return;
    }

    const draftResult = result.reason === 'process_error' || result.reason === 'error'
      ? await persistTaskFormDraft(taskId, data, 'validated')
      : null;
    const draftDescription = draftResult
      ? getDraftPersistenceDescription(draftResult.ok)
      : undefined;

    if (result.reason === 'task_not_active') {
      consumePendingLocalTaskAction(taskId, ['complete']);
      await showInactiveTaskAndLeave();
      return;
    }

    if (result.reason === 'conflict') {
      consumePendingLocalTaskAction(taskId, ['complete']);
      await handleTaskConflict('enviar', result.eventId);
      return;
    }

    if (result.reason === 'auth_error') {
      consumePendingLocalTaskAction(taskId, ['complete']);
      taskToast.warning('Sesion caducada', {
        description: appendErrorCode('Vuelve a iniciar sesion antes de enviar.', result.eventId),
      });
      return;
    }

    if (result.reason === 'process_error') {
      window.setTimeout(() => {
        consumePendingLocalTaskAction(taskId, ['complete']);
      }, 15000);
      taskToast.error('Camunda no pudo avanzar el proceso', {
        description: `${appendErrorCode(result.message, result.eventId)}\n${draftDescription}`,
        duration: 10000,
      });
      return;
    }

    consumePendingLocalTaskAction(taskId, ['complete']);
    taskToast.error('No se pudo enviar', {
      description: `${appendErrorCode('La tarea sigue abierta. Revisa el aviso del formulario e intentalo de nuevo.', result.eventId)}\n${draftDescription}`,
    });
  };

  const onError = async (errors: FieldErrors<FieldValues>) => {
    const validationErrorFields = collectValidationErrorFields(errors);
    const formValues = form.getValues();
    const processDefinitionKey = currentTask?.processDefinitionKey;

    if (formKey && processDefinitionKey && currentTask) {
      trackValidationFailed({
        action: 'send',
        bpm: {
          taskId,
          processInstanceId: currentTask.processInstanceId,
          processDefinitionId: currentTask.processDefinitionId,
          processDefinitionKey,
          processKey: processDefinitionKey,
          taskDefinitionKey: currentTask.taskDefinitionKey,
          formKey,
          assignee: currentTask.assignee ?? undefined,
        },
        form: {
          validationErrorFields,
          formValues,
          registeredFields: getRegisteredFormFields(form.control),
          dirtyFields,
          touchedFields,
          errors,
        },
      });
    }

    console.log(`Form validation errors for task: ${taskId}`, { validationErrorFields });

    const draftResult = await persistTaskFormDraft(taskId, formValues, 'input');
    taskToast.warning('No se pudo enviar el formulario', {
      description: `Revisa los campos indicados. ${getDraftPersistenceDescription(draftResult.ok)}`,
      duration: 10000,
    });
  };

  const handleSubmit = form.handleSubmit(onSubmit, onError);

  const handleRetryLoad = () => {
    if (!taskId) return;
    void selectTask(taskId)
      .then(async (result) => {
        if (result.status === 'ready') {
          await loadForm(taskId);
          return;
        }
        clearForm();
      })
      .catch((error) => {
        console.error('Error retrying task load:', error);
        clearForm();
      });
  };

  if (isLoading && !currentTask && !inactiveTaskNotice) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-sm text-gray-500">Verificando tarea...</div>
      </div>
    );
  }

  if (isLoading) {
    return <TaskPageSkeleton />;
  }

  if (submissionState === 'success' && SuccessComponent && submittedData) {
    return createElement(SuccessComponent, { submittedData });
  }

  if (error) {
    return <LoadingStates error={error} onRetry={handleRetryLoad} />;
  }

  if (!currentTask) {
    return (
      <div className="h-full" aria-live="polite">
        <span className="sr-only">
          {(inactiveTaskNotice ?? genericInactiveTaskNotice).title}
        </span>
      </div>
    );
  }

  if (!FormComponent) {
    if (!formInfo && !formError) {
      return <TaskPageSkeleton />;
    }
    return <LoadingStates error="Formulario no encontrado." />;
  }

  return (
    <FormProvider {...form}>
      <InheritedFormModeProvider>
        <div className={cn("flex flex-col h-full transition-all duration-200", isDisabled && "bg-gray-50")}>
          <TaskHeader task={currentTask} />
          <div className={cn("flex-1 overflow-auto p-6 transition-all duration-200", isDisabled && "bg-gray-50")}>
            <DisabledFormWrapper disabled={isDisabled}>
              <div className="min-h-full pb-2">
                {createElement(FormComponent, {
                  taskId,
                  processInstanceId: currentTask.processInstanceId,
                  variables,
                  taskDefinitionKey: currentTask.taskDefinitionKey,
                  preloadedData: preloadedData || undefined,
                  disabled: isDisabled,
                  hideAlerts: isDisabled,
                })}
              </div>
            </DisabledFormWrapper>
          </div>
          <TaskFooter
            onSave={handleSave}
            onSubmit={handleSubmit}
            isSaving={isSaving}
            isSubmitting={isSubmitting}
            processInstanceId={
              currentTask.processInstanceId !== currentTask.id ? currentTask.processInstanceId : undefined
            }
            disabled={isDisabled}
            submitDisabled={formKey === 'revision-gestion-cuentas' && !form.formState.isValid}
            submitLabel={formKey === 'revision-gestion-cuentas' ? 'Reanudar RPA' : undefined}
          />
        </div>
      </InheritedFormModeProvider>
    </FormProvider>
  );
}
